import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  Edit, 
  Mail, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  TrendingUp,
  Users,
  Building2,
  MapPin
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface Lead {
  id: string;
  businessName: string;
  contactName: string;
  email: string;
  phone: string;
  address: string;
  website?: string;
  socialMedia?: string;
  status: 'discovered' | 'enriched' | 'draft_created' | 'preview_sent' | 'approved' | 'live' | 'edited' | 'archived';
  discoveredDate: string;
  lastContactDate?: string;
  enrichmentScore: number;
  locationType: 'sauna' | 'cold_plunge' | 'wellness_center' | 'spa';
  estimatedRevenue?: number;
  notes?: string;
}

interface LeadStats {
  total: number;
  discovered: number;
  enriched: number;
  draftCreated: number;
  previewSent: number;
  approved: number;
  live: number;
  conversionRate: number;
}

const LeadManagementDashboard: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [stats, setStats] = useState<LeadStats>({
    total: 0,
    discovered: 0,
    enriched: 0,
    draftCreated: 0,
    previewSent: 0,
    approved: 0,
    live: 0,
    conversionRate: 0
  });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Mock data - replace with actual API calls
  useEffect(() => {
    const mockLeads: Lead[] = [
      {
        id: '1',
        businessName: 'Wild Wellness Retreat',
        contactName: 'Sarah O\'Connor',
        email: 'sarah@wildwellness.ie',
        phone: '+353 87 123 4567',
        address: 'Killarney, Co. Kerry',
        website: 'wildwellness.ie',
        status: 'discovered',
        discoveredDate: '2024-03-15',
        enrichmentScore: 85,
        locationType: 'wellness_center'
      },
      {
        id: '2',
        businessName: 'Mountain Sauna Co.',
        contactName: 'Mike Walsh',
        email: 'mike@mountainsauna.ie',
        phone: '+353 86 987 6543',
        address: 'Wicklow Mountains',
        status: 'enriched',
        discoveredDate: '2024-03-14',
        lastContactDate: '2024-03-16',
        enrichmentScore: 92,
        locationType: 'sauna',
        estimatedRevenue: 2500
      },
      {
        id: '3',
        businessName: 'Coastal Cold Plunge',
        contactName: 'Emma Byrne',
        email: 'emma@coastalplunge.ie',
        phone: '+353 85 456 7890',
        address: 'Dingle Peninsula',
        status: 'draft_created',
        discoveredDate: '2024-03-13',
        lastContactDate: '2024-03-15',
        enrichmentScore: 78,
        locationType: 'cold_plunge'
      }
    ];

    setLeads(mockLeads);
    
    // Calculate stats
    const total = mockLeads.length;
    const discovered = mockLeads.filter(l => l.status === 'discovered').length;
    const enriched = mockLeads.filter(l => l.status === 'enriched').length;
    const draftCreated = mockLeads.filter(l => l.status === 'draft_created').length;
    const previewSent = mockLeads.filter(l => l.status === 'preview_sent').length;
    const approved = mockLeads.filter(l => l.status === 'approved').length;
    const live = mockLeads.filter(l => l.status === 'live').length;
    
    setStats({
      total,
      discovered,
      enriched,
      draftCreated,
      previewSent,
      approved,
      live,
      conversionRate: total > 0 ? Math.round((live / total) * 100) : 0
    });
  }, []);

  const getStatusBadge = (status: string) => {
    const variants = {
      discovered: { variant: 'secondary', icon: Clock, label: 'Discovered' },
      enriched: { variant: 'default', icon: TrendingUp, label: 'Enriched' },
      draft_created: { variant: 'outline', icon: Edit, label: 'Draft Created' },
      preview_sent: { variant: 'default', icon: Mail, label: 'Preview Sent' },
      approved: { variant: 'default', icon: CheckCircle, label: 'Approved' },
      live: { variant: 'default', icon: CheckCircle, label: 'Live' },
      edited: { variant: 'outline', icon: Edit, label: 'Edited' },
      archived: { variant: 'destructive', icon: AlertCircle, label: 'Archived' }
    } as const;

    const config = variants[status as keyof typeof variants];
    const Icon = config.icon;

    return (
      <Badge variant={config.variant as any} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const getLocationTypeIcon = (type: string) => {
    const icons = {
      sauna: Building2,
      cold_plunge: MapPin,
      wellness_center: Users,
      spa: Building2
    };
    const Icon = icons[type as keyof typeof icons];
    return <Icon className="w-4 h-4" />;
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = lead.businessName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         lead.contactName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         lead.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Lead Management</h1>
          <p className="text-muted-foreground">Discover, enrich, and convert leads into live listings</p>
        </div>
        <Button className="bg-sauna-orange hover:bg-sauna-orange/90">
          <Plus className="w-4 h-4 mr-2" />
          Add Lead Manually
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">
              +12% from last month
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Pipeline</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.discovered + stats.enriched + stats.draftCreated}</div>
            <p className="text-xs text-muted-foreground">
              {stats.previewSent} previews sent
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Live Listings</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.live}</div>
            <p className="text-xs text-muted-foreground">
              {stats.conversionRate}% conversion rate
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Revenue</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">€2,450</div>
            <p className="text-xs text-muted-foreground">
              Per live listing
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">All Leads ({stats.total})</TabsTrigger>
          <TabsTrigger value="discovered">Discovered ({stats.discovered})</TabsTrigger>
          <TabsTrigger value="enriched">Enriched ({stats.enriched})</TabsTrigger>
          <TabsTrigger value="draft">Draft Created ({stats.draftCreated})</TabsTrigger>
          <TabsTrigger value="preview">Preview Sent ({stats.previewSent})</TabsTrigger>
          <TabsTrigger value="live">Live ({stats.live})</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search leads..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="discovered">Discovered</SelectItem>
                    <SelectItem value="enriched">Enriched</SelectItem>
                    <SelectItem value="draft_created">Draft Created</SelectItem>
                    <SelectItem value="preview_sent">Preview Sent</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="live">Live</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Leads Table */}
          <Card>
            <CardHeader>
              <CardTitle>Lead Pipeline</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Business</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Enrichment Score</TableHead>
                    <TableHead>Discovered</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{lead.businessName}</div>
                          <div className="text-sm text-muted-foreground">{lead.address}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{lead.contactName}</div>
                          <div className="text-sm text-muted-foreground">{lead.email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getLocationTypeIcon(lead.locationType)}
                          <span className="capitalize">{lead.locationType.replace('_', ' ')}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(lead.status)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-sauna-orange h-2 rounded-full" 
                              style={{ width: `${lead.enrichmentScore}%` }}
                            />
                          </div>
                          <span className="text-sm">{lead.enrichmentScore}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-muted-foreground">
                          {new Date(lead.discoveredDate).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm">
                            <Mail className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Other tab contents would be similar but filtered by status */}
        <TabsContent value="discovered" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <p className="text-muted-foreground">Leads that have been auto-discovered and need enrichment</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LeadManagementDashboard; 